import { useQueryClient } from '@tanstack/react-query'
import { includes } from 'lodash'
import { useCallback, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import useSignoutContext from '~/features/auth/hooks/useSignoutContext'
import useGetPartnerConfigByOrgId from '~/features/partners/hooks/useGetPartnerConfigByOrgId'
import useIsPartner from '~/features/partners/hooks/useIsPartner'
import { EPartnerDomains } from '~/features/partners/models'
import {
  isLibbyDomain,
  queries as partnerQueries,
} from '~/features/partners/utils'
import { EClientPath, EMarketingClientPath } from '~/models/api'
import { TNavigateState } from '~/models/auth'
import AuthService from '~/services/auth'
import { store } from '~/store'

export type THardSignOutOptions = {
  /** Passed to login navigation (e.g. validatedUserErrorMessage). */
  navigateState?: TNavigateState
  /** Skip post-logout navigation (caller handles it). */
  skipNavigation?: boolean
}

/** Single-flight so concurrent auth failures share one Cognito + app clear + navigate. */
let hardSignOutInFlight: Promise<void> | null = null

export const isHardSignOutInFlight = () => hardSignOutInFlight !== null

/**
 * Canonical hard logout: Cognito global sign-out, app/query clear via
 * SignoutContextProvider (isSigningOut), then a single navigation.
 * All auth-failure paths should call this instead of ad-hoc clears.
 */
const useSignOut = () => {
  const [isLoading, setIsLoading] = useState(false)

  const orgId = store((state) => state.user.organizationId)
  const { data: partnerData } = useGetPartnerConfigByOrgId({ orgId })
  const queryClient = useQueryClient()
  const { setIsSigningOut } = useSignoutContext()

  const navigate = useNavigate()
  const { pathname } = useLocation()

  const isPartner = useIsPartner() || !isLibbyDomain()

  const isMarketingPath = includes(
    Object.values(EMarketingClientPath),
    pathname,
  )

  const signOut = useCallback(
    async (options: THardSignOutOptions = {}) => {
      if (hardSignOutInFlight) {
        return hardSignOutInFlight
      }

      hardSignOutInFlight = (async () => {
        try {
          setIsLoading(true)

          try {
            await AuthService.signOut()
          } catch (error) {
            // Global sign-out may fail (network, already signed out); continue
            // with local cleanup / navigation regardless.
            // eslint-disable-next-line no-console
            console.error('auth_hard_sign_out_cognito_failed', {
              error,
              pathname,
            })
          }

          // Hub `signedOut` usually sets this; ensure app/query clear starts
          // even if Cognito sign-out threw before dispatching Hub.
          setIsSigningOut(true)

          if (options.skipNavigation) {
            return
          }

          let partnerDomain: string = partnerData?.data?.domain || ''

          if (!partnerDomain) {
            try {
              partnerDomain =
                (
                  await queryClient.fetchQuery({
                    ...partnerQueries.partnerByDomain(true),
                    staleTime: 0,
                  })
                )?.data?.domain || ''
            } catch {
              // Partner lookup is best-effort for OIDC end-session only.
            }
          }

          if (
            [
              EPartnerDomains.virtualGurus,
              EPartnerDomains.whiteLabelChris,
            ].some((link) => partnerDomain.includes(link))
          ) {
            window.location.href = import.meta.env
              .VITE_VIRTUAL_GURUS_OIDC_END_SESSION_URL as string
            return
          }

          if (!isMarketingPath) {
            navigate(isPartner ? EClientPath.partnerAuth : EClientPath.login, {
              replace: true,
              state: options.navigateState,
            })
          }
        } finally {
          setIsLoading(false)
          // Defer clearing so callers that `await signOut()` then throw into a
          // sibling `.catch` still see the in-flight latch and join this logout.
          Promise.resolve().then(() => {
            hardSignOutInFlight = null
          })
        }
      })()

      return hardSignOutInFlight
    },
    [
      isMarketingPath,
      isPartner,
      navigate,
      partnerData?.data?.domain,
      pathname,
      queryClient,
      setIsSigningOut,
    ],
  )

  return { isLoading, signOut }
}

export default useSignOut
